import Lenis from "lenis";
import "lenis/dist/lenis.css";
import { createDarkVeil } from "./dark-veil.js";

document.documentElement.classList.add("js");

const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

// Initialize Lenis smooth inertia scrolling
let lenis = null;
if (!reducedMotion.matches) {
  lenis = new Lenis({
    duration: 1.2,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
  });

  function raf(time) {
    lenis.raf(time);
    requestAnimationFrame(raf);
  }
  requestAnimationFrame(raf);

  // Smooth scroll for internal anchor links
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener("click", (e) => {
      const href = anchor.getAttribute("href");
      if (!href || href === "#") return;
      const targetEl = document.querySelector(href);
      if (targetEl) {
        e.preventDefault();
        lenis.scrollTo(targetEl, { offset: -24, duration: 1.4 });
      }
    });
  });
}

const body = document.body;
const revealItems = [...document.querySelectorAll(".reveal")];

function updateNavigation() {
  body.classList.toggle("is-scrolled", window.scrollY > 24);
}

updateNavigation();
window.addEventListener("scroll", updateNavigation, { passive: true });

if ("IntersectionObserver" in window && !reducedMotion.matches) {
  const revealObserver = new IntersectionObserver(
    (entries, observer) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add("is-visible");
        observer.unobserve(entry.target);
      }
    },
    { rootMargin: "0px 0px -8%", threshold: 0.08 },
  );

  revealItems.forEach((item) => revealObserver.observe(item));
} else {
  revealItems.forEach((item) => item.classList.add("is-visible"));
}

const heroCanvas = document.querySelector("#hero-canvas");
const hero = document.querySelector(".hero");

const SPEED = 1.0;

// Dark Veil background canvas configuration with gradual hue shift and scanlines
const veil = heroCanvas
  ? createDarkVeil(heroCanvas, {
      scanlineFrequency: 1.6,
      scanlineIntensity: 0.2,
    })
  : null;

if (veil && hero) {
  body.classList.add("canvas-ready");

  let animationFrame = 0;
  let isVisible = true;
  let start = performance.now();
  let elapsed = 0;

  function resizeCanvas() {
    const bounds = hero.getBoundingClientRect();
    veil.resize(Math.max(1, Math.round(bounds.width)), Math.max(1, Math.round(bounds.height)));
  }

  function render(now) {
    if (!isVisible || document.hidden) {
      animationFrame = 0;
      return;
    }

    elapsed = ((now - start) / 1000) * SPEED;
    const hueShift = (elapsed * 6) % 360;
    veil.render(elapsed, hueShift);
    animationFrame = requestAnimationFrame(render);
  }

  function startRendering() {
    if (animationFrame || !isVisible || document.hidden || reducedMotion.matches) return;
    // Rebase so time does not jump forward by however long the hero was hidden.
    start = performance.now() - (elapsed / SPEED) * 1000;
    animationFrame = requestAnimationFrame(render);
  }

  function stopRendering() {
    cancelAnimationFrame(animationFrame);
    animationFrame = 0;
  }

  function drawStillFrame() {
    stopRendering();
    veil.render(elapsed, (elapsed * 6) % 360);
  }

  const resizeObserver = new ResizeObserver(() => {
    resizeCanvas();
    if (reducedMotion.matches) drawStillFrame();
    else startRendering();
  });
  resizeObserver.observe(hero);

  const visibilityObserver = new IntersectionObserver(
    ([entry]) => {
      isVisible = entry.isIntersecting;
      if (isVisible) startRendering();
      else stopRendering();
    },
    { threshold: 0.01 },
  );
  visibilityObserver.observe(hero);

  reducedMotion.addEventListener("change", () => {
    if (reducedMotion.matches) drawStillFrame();
    else startRendering();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stopRendering();
    else startRendering();
  });

  resizeCanvas();
  if (reducedMotion.matches) drawStillFrame();
  else startRendering();
}

// Stack category filter
const stackFilters = document.querySelectorAll(".stack-filter");
const stackGroups = document.querySelectorAll(".stack-group");

if (stackFilters.length > 0 && stackGroups.length > 0) {
  stackFilters.forEach((btn) => {
    btn.addEventListener("click", () => {
      const filter = btn.dataset.filter;
      stackFilters.forEach((b) => {
        const isActive = b === btn;
        b.classList.toggle("is-active", isActive);
        b.setAttribute("aria-selected", isActive ? "true" : "false");
      });
      stackGroups.forEach((group) => {
        const match = filter === "all" || group.dataset.category === filter;
        group.style.display = match ? "" : "none";
      });
    });
  });
}

// ---------------------------------------------------------------------------
// Easter egg: console banner + IDDQD / IDKFA cheat codes
// ---------------------------------------------------------------------------
(function doomEasterEgg() {
  const logo = String.raw`
 ███████╗██╗   ██╗██████╗
 ╚══███╔╝██║   ██║██╔══██╗
   ███╔╝ ██║   ██║██████╔╝
  ███╔╝  ██║   ██║██╔═══╝
 ███████╗╚██████╔╝██║
 ╚══════╝ ╚═════╝ ╚═╝
`;

  console.log(
    `%c${logo}`,
    "color:#7257ff; font-family:monospace; font-weight:bold; text-shadow:0 0 6px #7257ff;",
  );
  console.log(
    "%cSYSTEM ONLINE",
    "color:#58d6ff; font-family:monospace; font-size:12px; letter-spacing:3px;",
  );
  console.log(
    "%cLooking for a way in? Some codes never go out of style. IDDQD. IDKFA.",
    "color:#8a8a9a; font-family:monospace; font-size:11px;",
  );
  console.log(
    "%cLike poking around under the hood? So do we. info@zup.ee",
    "color:#ff708a; font-family:monospace; font-size:11px;",
  );
  fetch("https://api.ipify.org?format=json")
    .then((res) => res.json())
    .then((data) => {
      console.log(
        `%cYour ip: ${data.ip}`,
        "color:#ff708a; font-family:monospace; font-size:11px;",
      );
    })
    .catch(() => {});

  const CHEATS = {
    iddqd: {
      color: "57,255,20",
      accent: "#39ff14",
      lines: ["IDDQD", "Degreelessness mode ON — you are now invincible."],
    },
    idkfa: {
      color: "255,183,3",
      accent: "#ffb703",
      lines: ["IDKFA", "Keys, weapons, and ammo for all weapons acquired."],
    },
  };

  let buffer = "";
  let flashTimer = null;

  function flashScreen(rgb) {
    let overlay = document.getElementById("cheat-flash");
    if (!overlay) {
      overlay = document.createElement("div");
      overlay.id = "cheat-flash";
      overlay.style.cssText =
        "position:fixed;inset:0;z-index:99999;pointer-events:none;opacity:0;" +
        "transition:opacity 160ms ease-out;mix-blend-mode:screen;";
      document.body.appendChild(overlay);
    }
    overlay.style.background = `rgba(${rgb},0.35)`;
    overlay.style.opacity = "1";
    clearTimeout(flashTimer);
    flashTimer = setTimeout(() => {
      overlay.style.opacity = "0";
    }, 200);
  }

  window.addEventListener("keydown", (e) => {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const key = e.key.toLowerCase();
    if (key.length !== 1 || key < "a" || key > "z") {
      buffer = "";
      return;
    }
    buffer = (buffer + key).slice(-5);

    const cheat = CHEATS[buffer];
    if (!cheat) return;
    buffer = "";

    const style = `color:${cheat.accent}; font-family:monospace; font-size:16px; font-weight:bold; text-shadow:0 0 8px ${cheat.accent};`;
    console.log(`%c${cheat.lines[0]}`, style);
    console.log(
      `%c${cheat.lines[1]}`,
      "color:#8a8a9a; font-family:monospace; font-size:11px;",
    );
    flashScreen(cheat.color);
  });
})();

